<template>
	<div>
		<v-menu auto offset-y transition="slide-y-transition">
			<template v-slot:activator="{ on, attrs }">
				<v-btn icon v-bind="attrs" v-on="on" color="grey darken-2" @click="dialog = true">
					<v-icon color="grey darken-2">mdi-help-circle-outline</v-icon>
				</v-btn>
			</template>
			<v-dialog v-model="dialog" :width="windowInnerWidth">
				<v-card flat>
					<v-card-title class="primary white--text">
						Help manual
						<v-spacer></v-spacer>
						<v-btn icon dark @click="dialog = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text class="mt-5">
						<h3 class="mb-2">Data warehouse</h3>
						<v-img :contain="true" src="../../public/search-index.png" alt="search-index" />
						<div>
							In the first column of search fields we have the ones covering the geographic location.
						</div>
						<br />

						<div>
							We could start by typing what we would like to search for or just click in the box to see
							the available options for us.
						</div>
						<br />
						<ul>
							<li>
								If no values are selected in these fields, the search will be performed on a country
								level for all countries.
							</li>
							<li>
								If selecting a specific country, the search will be performed on a country level for
								that specific country.
							</li>
							<li>
								If we want to dive deeper in the search and specify sales for regions and/or bricks,
								select the value <code>All</code> in <code>Region</code> or <code>Brick</code>.
							</li>
							<li>
								If selecting for example Sweden in
								<code>Country</code>, only regions in Sweden will be available in
								<code>Region</code> and if selecting Stockholm in <code>Region</code>, only bricks
								within Stockholm will be available in <code>Brick</code>.
							</li>
						</ul>
						<br />
						<div>The next 9 fields are regarding the product we want to search for.</div>
						<v-img :contain="true" src="../../public/search-fields.png" alt="search-fields" />
						<br />
						<div>
							<h4>ATC Code</h4>
							To select an ATC code, start by typing at least three characters and we will get a list of
							matching ATC codes. If we for example want all products within
							<code>A01</code>, we could select that one from the list or select specific ATC codes within
							that group. When the list shows up we can press <code>Enter</code> to select all ATC codes
							that are in the list or just select the ones we are interested in. When we have entered the
							ATC code we would like to search for, the related substances will appear in the
							<code>Substance</code> field.
						</div>
						<br />
						<div>
							<h4>Substance</h4>
							To select a substance, start by typing at least three characters and we will get a list of
							matching substances. Only the Swedish version of the substance name is available. When the
							list shows up we can press <code>Enter</code> to select all substances that are in the list
							or just select the ones we are interested in. When we have entered the substances we would
							like to search for, the related ATC codes will appear in the <code>ATC Code</code> field.
						</div>
						<br />
						<div>
							<h4>Product</h4>
							In this field, we have the option to search for a specific product name. To select a
							product, start by typing at least three characters and we will get a list of matching
							products. When the list shows up we can press <code>Enter</code> to select all products that
							are in the list or just select the ones we are interested in.
						</div>
						<br />
						<div>
							<h4>Formulation, Strength & Size</h4>
							These three fields are connected to <code>ATC Code</code>, <code>Substance</code> and
							<code>Product</code> so available options will only be those given for products within the
							selection made. By clicking in the field, available options will be displayed. We can also
							enter what we want to select to filter the list. For example, if typing in the value
							<code>Tablet</code>, only formulations containing that text will be displayed. When the list
							shows up we can press <code>Enter</code> to select all formulations, strengths or sizes that
							are in the list or just select the ones we are interested in.
						</div>
						<br />
						<div>
							<h4>Nordic Product no.</h4>
							Here we can enter a specific <code>Nordic Product Number</code> or as many as we like to
							search for. When the list shows up we can press <code>Enter</code> to select all nordic
							product numbers that are in the list or just select the ones we are interested in.
						</div>
						<br />
						<div>
							<h4>Company</h4>
							We can start by typing the first three characters of the company we would like to select and
							a list with matching ones will be displayed. When the list shows up we can press
							<code>Enter</code> to select all companies that are in the list or just select the ones we
							are interested in.
						</div>
						<br />
						<div>
							<h4>Measurement</h4>
							The <code>Measurement</code> field is where we can decide what type of data we would like to
							see. When the list shows up we can press <code>Enter</code> to select all measurements that
							are in the list or just select the ones we are interested in.
						</div>
						<br />
						<div>
							Here we can select what measurements we would like to look at. There are 8 different types:
						</div>
						<ul>
							<li><code class="mr-1">Qty</code><i class="caption">(Number of packs sold)</i></li>
							<li><code class="mr-1">DDD</code><i class="caption">(Daily Defined Doses sold)</i></li>
							<li>
								<code class="mr-1">AIP</code>
								<i class="caption">(Pharmacy purchase price or hospital contract price)</i>
							</li>
							<li><code class="mr-1">AUP</code><i class="caption">(Pharmacy sell out price)</i></li>
							<li>
								<code class="mr-1">Value AIP</code>
								<i class="caption">(Sell in value of the sold packs in AIP)</i>
							</li>
							<li>
								<code class="mr-1">Value AUP</code>
								<i class="caption">(Sell in value of the sold packs in AUP)</i>
							</li>
							<li>
								<code class="mr-1">SO Value AIP</code>
								<i class="caption">(Sell out value of the sold packs in AIP)</i>
							</li>
							<li>
								<code class="mr-1">SO Value AUP</code>
								<i class="caption">(Sell out value of the sold packs in AUP)</i>
							</li>
						</ul>
						<br />
						<div>
							For each of these measurements there are five different versions depending on time span:
						</div>
						<ul>
							<li>
								<code class="mr-1">Qty</code
								><i class="caption">(Gives the figure for selected month)</i>
							</li>
							<li>
								<code class="mr-1">Qty MAT</code>
								<i class="caption">(Gives the Moving Annual Total calculated for selected month)</i>
							</li>
							<li>
								<code class="mr-1">Qty MAT-1</code>
								<i class="caption">
									(Gives the Moving Annual Total minus one year calculated for selected month minus
									one year)
								</i>
							</li>
							<li>
								<code class="mr-1">Qty YTD</code>
								<i class="caption">(Gives the Year To Date total calculated for selected month)</i>
							</li>
							<li>
								<code class="mr-1">Qty YTD-1</code>
								<i class="caption">
									(Gives the Year To Date total calculated for selected month minus one year)
								</i>
							</li>
						</ul>
						<br />
						<div>If we for example were to do a search on September 2018, this would be our result:</div>
						<ul>
							<li>
								<code class="mr-1">Qty</code>
								<i class="caption">(Quantity sold in September 2018)</i>
							</li>
							<li>
								<code class="mr-1">Qty MAT</code>
								<i class="caption">(Total quantity sold October 2017 - September 2018)</i>
							</li>
							<li>
								<code class="mr-1">Qty MAT-1</code>
								<i class="caption">(Total quantity sold October 2016 - September 2017)</i>
							</li>
							<li>
								<code class="mr-1">Qty YTD</code>
								<i class="caption">(Total quantity sold January 2018 - September 2018)</i>
							</li>
							<li>
								<code class="mr-1">Qty YTD-1</code>
								<i class="caption">(Total quantity sold January 2017 - September 2017)</i>
							</li>
						</ul>
						<br />
						<div>
							<h4>Period, Year & Month</h4>
							The last three fields are for which dates we want to search within.
							<v-img
								:contain="true"
								max-height="250"
								max-width="250"
								src="../../public/search-dates.png"
								alt="search-dates"
							/>
							<br />
							In <code>Period</code>, we can select four different periods that are always calculated from
							the previous month which are:
						</div>
						<ul>
							<li><code>MAT</code></li>
							<li><code>MAT-1</code></li>
							<li><code>YTD</code></li>
							<li><code>YTD-1</code></li>
						</ul>
						<br />
						<div>
							This overrides <code>Year</code> and <code>Month</code> and vice versa so we can’t make
							selections in both. The difference from selecting MAT figures in this field instead of in
							<code>Measurement</code> is that in <code>Measurement</code> we get the MAT in total and in
							the <code>Period</code> field we get it as the selected period.
						</div>
						<br />
						<div>For example, given that the current month is October 2018:</div>
						<ul>
							<li>
								Searching for <code>Qty</code> in <code>Measurement</code> and <code>MAT</code> in
								<code>Period</code> will give the quantity for each month between October 2017 –
								September 2018.
							</li>
							<li>
								Searching for <code>Qty MAT</code> in <code>Measurement</code> and
								<code>Current year</code> in <code>Year</code> and <code>Previous month</code> in
								<code>Month</code> will give the total quantity for the whole period October 2017 –
								September 2018.
							</li>
						</ul>
						<br />

						<div>
							<h3>Result</h3>
							The result is displayed in the same manner as a pivot table.
							<v-img :contain="true" src="../../public/search-pvt-chart.jpg" alt="search-pivot-chart" />
						</div>
						<br />
						<div>
							The left one of the two first columns show possible product properties to display and the
							right column shows the one we have selected. We can drag and drop the properties between
							each other and also between the X and Y axis. All possible properties have an arrow beside
							their name, clicking on that one enables us to filter the result.
						</div>
						<br />
						<v-img
							:contain="true"
							max-height="250"
							max-width="250"
							src="../../public/search-filter.png"
							alt="search-filter"
						/>
						<br />
						<div>
							We also have two buttons to manipulate the pivot table and a third button to export the
							table that is being shown. The <code>Total</code> button displays the total sum for both
							rows and columns, and the <code>Rounded</code> button rounds all values that are inside the
							table.
						</div>
						<br />
						<div>A few of the properties needs some more explanation:</div>
						<ul>
							<li>
								<code class="mr-1">Sales channel</code>
								<i class="caption">(If sold for hospital use or at a pharmacy)</i>
							</li>
							<li>
								<code class="mr-1">Sales type</code>
								<i class="caption">(If sold as RX, OTC or for hospital use)</i>
							</li>
							<li>
								<code class="mr-1">NPL Pack ID</code>
								<i class="caption">(Unique code for all Swedish products)</i>
							</li>
							<li><code class="mr-1">VNR</code> <i class="caption">(Nordic Product Number)</i></li>
							<li>
								<code class="mr-1">PSG</code>
								<i class="caption">(Pack Size Group, used for generic products only)</i>
							</li>
							<li>
								<code class="mr-1">EGID</code>
								<i class="caption">(Exchange Group Id, used for generic products only)</i>
							</li>
						</ul>
						<v-list-item two-line class="mt-7 contact">
							<v-list-item-content class="text-center py-7">
								<v-list-item-title class="font-weight-medium">Contact us at</v-list-item-title>
								<v-list-item-subtitle>
									<v-icon color="primary" small left>mdi-email</v-icon>
									<a
										class="text-decoration-none"
										target="_blank"
										href="mailto: support@bonnierpharmainsights.se"
										>support@bonnierpharmainsights.se
									</a>
								</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
					</v-card-text>
				</v-card>
			</v-dialog>
		</v-menu>
	</div>
</template>

<script>
	/**
	 * @module Help
	 *
	 * @vue-data {boolean} [dialog=false] - Toggle dialog
	 * @vue-computed {number} windowInnerWidth - Sets the inner width of the window object
	 */

	export default {
		data() {
			return {
				dialog: false,
			};
		},
		computed: {
			windowInnerWidth() {
				return window.innerWidth / 2;
			},
		},
	};
</script>

<style lang="scss" scoped>
	.contact {
		background: rgba(0, 0, 0, 0.05);
		border-radius: 5px;

		& .v-list-item__title {
			color: rgba(0, 0, 0, 0.6) !important;
		}
	}
</style>
